import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useState} from "react";

import {IntegerField} from "@khanacademy/event-schemas";
import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import SearchField from "@khanacademy/wonder-blocks-search-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    publishAnalyticsEventForMinorConversion,
    useMarkConversion,
} from "@khan/analytics-learning-time";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import {useAvailableTeacherTools} from "./hooks/use-available-teacher-tools.ts";
import {LanguageAvailabilityBanner} from "./language-availability-banner.tsx";
import {ToolCardsEmptyState} from "./tool-cards-empty-state.tsx";
import {ToolCards} from "./tool-cards.tsx";
import {ToolCategoryFilters} from "./tool-category-filters.tsx";
import {ToolCategories, ToolPersona, type Platform} from "./types.ts";
import {filterToolsByCategoryAndSearch} from "./utils/filtering.ts";

interface Props {
    platform?: Platform;
}

/**
 * Component to render the _teacher_ tools cards. This component assumes that
 * the current user is a teacher, so we query for the teacher-persona tools
 * and implement teacher-persona-specific behavior here. The lower-level
 * components like `ToolCards` are persona-agnostic.
 *
 * If we want to implement a parent-persona version of this, we should create
 * a new component similar to this one that makes a parent-specific query.
 */
export const TeacherToolsCards = ({platform}: Props) => {
    const markConversion = useMarkConversion();

    const [category, setCategory] = useState<ToolCategories>(
        ToolCategories.All,
    );
    const [searchTerm, setSearchTerm] = useState<string>("");

    const {
        toolLoadingStatus,
        availableTools,
        favoriteToolConfigNames,
        addFavorite,
        deleteFavorite,
    } = useAvailableTeacherTools();

    const filteredTools = filterToolsByCategoryAndSearch(
        availableTools,
        favoriteToolConfigNames,
        category,
        searchTerm,
    );

    const categoriesToRender = [
        // These two are always shown first:
        ToolCategories.All,
        ToolCategories.Favorites,
        // And then we show the categories pulled from the available tools:
        ...new Set(availableTools.map((tool) => tool.categories[0])),
    ];

    const handleBlur = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiTeacherToolsToolSearched",
            eventSchemaVersion: 1,
            searchTerm: searchTerm,
            numMatches: new IntegerField(filteredTools.length),
        });
    };

    const updateToolFavorited = useCallback(
        (configName: string, nowFavorited: boolean) => {
            if (nowFavorited) {
                addFavorite(configName);
            } else {
                deleteFavorite(configName);
            }
        },
        [addFavorite, deleteFavorite],
    );

    useOnMountEffect(() => {
        markConversion({id: "pageview_khanmigo_teacher_tools_dashboard"});
    });

    return (
        <View>
            <LanguageAvailabilityBanner
                kaLocale={RequestInfo.kaLocale}
                persona={ToolPersona.Teacher}
            />
            <View style={styles.filter}>
                <ToolCategoryFilters
                    selected={category}
                    setSelected={setCategory}
                    categoriesToRender={categoriesToRender}
                />
                <SearchField
                    value={searchTerm}
                    onChange={setSearchTerm}
                    style={styles.search}
                    placeholder={t`Search`}
                    onBlur={handleBlur}
                />
            </View>
            {toolLoadingStatus === "loading" ? (
                <CircularSpinner style={styles.spinner} />
            ) : filteredTools.length === 0 ? (
                <ToolCardsEmptyState
                    errorType={
                        category === ToolCategories.Favorites
                            ? "noFavorites"
                            : "noResult"
                    }
                />
            ) : (
                <ToolCards
                    filteredTools={filteredTools}
                    category={category}
                    updateToolFavorited={updateToolFavorited}
                    platform={platform}
                    persona={ToolPersona.Teacher}
                    favoriteToolConfigNames={favoriteToolConfigNames}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    filter: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBlockEnd: sizing.size_320,
        [mediaQueries.mdOrSmaller]: {
            alignItems: "stretch",
            flexDirection: "column",
            marginBlockEnd: sizing.size_160,
        },
    },
    search: {
        [mediaQueries.mdOrSmaller]: {
            marginBlockStart: sizing.size_160,
        },
    },
    spinner: {
        alignSelf: "center",
        paddingBlock: sizing.size_800,
    },
});
